import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Eraser, PenLine, Type as TypeIcon } from "lucide-react";

const FONTS = [
  { key: "cursive", label: "Signature", css: '"Snell Roundhand", "Apple Chancery", cursive' },
  { key: "italic", label: "Italic", css: '"Georgia", serif' },
  { key: "mono", label: "Print", css: '"Courier New", monospace' },
];

/**
 * Dual-mode signature capture. Consumers pass:
 *  - mode: 'draw' | 'type' | 'either'   (form's allowed modes)
 *  - value: {mode, value, font?}         (controlled)
 *  - onChange: (payload) => void
 */
export default function SignaturePad({ mode = "either", value, onChange, defaultName = "" }) {
  const canRef = useRef(null);
  const [active, setActive] = useState(value?.mode || (mode === "type" ? "type" : "draw"));
  const [typed, setTyped] = useState(value?.mode === "type" ? value.value : defaultName);
  const [font, setFont] = useState(value?.font || FONTS[0].key);
  const drawingRef = useRef(false);

  // Sync typed sig outward
  useEffect(() => {
    if (active !== "type") return;
    if (!typed) { onChange?.(null); return; }
    onChange?.({ mode: "type", value: typed, font });
  }, [typed, font, active, onChange]);

  // Canvas setup
  useEffect(() => {
    if (active !== "draw") return;
    const c = canRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [active]);

  const pos = (e) => {
    const c = canRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };
  const start = (e) => { e.preventDefault(); drawingRef.current = true; const { x, y } = pos(e); const ctx = canRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawingRef.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const dataUrl = canRef.current.toDataURL("image/png");
    onChange?.({ mode: "draw", value: dataUrl });
  };
  const clear = () => {
    const c = canRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    onChange?.(null);
  };

  const showTabs = mode === "either";
  return (
    <div data-testid="signature-pad" className="rounded-md border border-white/10 bg-[#0A0A0A] p-3">
      {showTabs && (
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            data-testid="sig-tab-draw"
            onClick={() => { setActive("draw"); onChange?.(null); }}
            className={`text-xs px-3 h-8 rounded inline-flex items-center gap-1.5 border transition-colors ${active === "draw" ? "bg-gold text-black border-gold" : "border-white/10 text-zinc-300 hover:bg-white/5"}`}
          ><PenLine className="w-3.5 h-3.5" /> Draw</button>
          <button
            type="button"
            data-testid="sig-tab-type"
            onClick={() => { setActive("type"); onChange?.(null); }}
            className={`text-xs px-3 h-8 rounded inline-flex items-center gap-1.5 border transition-colors ${active === "type" ? "bg-gold text-black border-gold" : "border-white/10 text-zinc-300 hover:bg-white/5"}`}
          ><TypeIcon className="w-3.5 h-3.5" /> Type</button>
        </div>
      )}

      {active === "draw" ? (
        <>
          <canvas
            ref={canRef}
            data-testid="sig-canvas"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            style={{ width: "100%", height: "140px", touchAction: "none" }}
            className="rounded border border-dashed border-white/15 bg-[#121212] cursor-crosshair"
          />
          <div className="mt-2 flex items-center justify-between">
            <div className="text-[11px] text-zinc-500">Sign above with your mouse or finger.</div>
            <button
              type="button"
              data-testid="sig-clear"
              onClick={clear}
              className="text-[11px] inline-flex items-center gap-1 text-zinc-400 hover:text-white"
            ><Eraser className="w-3 h-3" /> Clear</button>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <Input
            data-testid="sig-typed-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type your full legal name"
            className="bg-[#121212] border-white/10"
          />
          <RadioGroup value={font} onValueChange={setFont} className="flex gap-2 flex-wrap">
            {FONTS.map((f) => (
              <label
                key={f.key}
                htmlFor={`sig-font-${f.key}`}
                className={`cursor-pointer rounded border px-3 py-2 text-lg leading-none flex-1 min-w-[100px] text-center ${font === f.key ? "border-gold text-white bg-gold/10" : "border-white/10 text-zinc-400 hover:border-white/20"}`}
                style={{ fontFamily: f.css }}
              >
                <RadioGroupItem
                  id={`sig-font-${f.key}`}
                  value={f.key}
                  className="sr-only"
                  data-testid={`sig-font-${f.key}`}
                />
                {typed || f.label}
              </label>
            ))}
          </RadioGroup>
          <div className="text-[11px] text-zinc-500">
            Typing your name here has the same legal effect as a handwritten signature.
          </div>
        </div>
      )}
    </div>
  );
}
